import { useEffect, useId, useMemo, useRef, useState, type ReactNode, type TouchEvent } from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";

import { defaultIconProps } from "../features/shared/ui/icons";
import { useResponsiveBreakpoint } from "../hooks/useResponsiveBreakpoint";

interface SelectionModalProps {
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
  variant?: "auto" | "dialog" | "bottom-sheet";
}

export function SelectionModal({
  title,
  closeLabel,
  onClose,
  children,
  variant = "auto",
}: SelectionModalProps) {
  const titleId = useId();
  const modalRoot = typeof document !== "undefined" ? document.body : null;
  const { isMobile } = useResponsiveBreakpoint();
  const computedVariant = useMemo(() => {
    if (variant === "auto") {
      return isMobile ? "bottom-sheet" : "dialog";
    }
    return variant;
  }, [isMobile, variant]);
  const dragStartY = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (!modalRoot) {
    return null;
  }

  const overlayClasses = [
    "fixed inset-0 z-50 flex justify-center bg-slate-900/70 px-4 py-6 text-slate-900 backdrop-blur-sm transition",
    computedVariant === "bottom-sheet" ? "items-end" : "items-center",
  ].join(" ");

  const panelClasses = [
    "w-full rounded-3xl bg-white shadow-2xl ring-1 ring-slate-100 focus:outline-none dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100",
    computedVariant === "bottom-sheet"
      ? "max-w-2xl pb-safe"
      : "max-w-md",
  ].join(" ");

  const contentClasses =
    "max-h-[70vh] overflow-y-auto px-4 py-4 sm:px-6 sm:py-6";

  const handleTouchStart = (event: TouchEvent) => {
    if (computedVariant !== "bottom-sheet") {
      return;
    }
    dragStartY.current = event.touches[0]?.clientY ?? null;
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (computedVariant !== "bottom-sheet") {
      return;
    }
    if (dragStartY.current == null) {
      return;
    }
    const delta = event.touches[0]?.clientY ?? dragStartY.current;
    const offset = Math.max(0, delta - dragStartY.current);
    setDragOffset(offset);
  };

  const handleTouchEnd = () => {
    if (computedVariant !== "bottom-sheet") {
      return;
    }
    if (dragOffset > 80) {
      onClose();
    }
    setDragOffset(0);
    dragStartY.current = null;
  };

  return createPortal(
    <div
      className={overlayClasses}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={panelClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={computedVariant === "bottom-sheet" ? { transform: `translateY(${dragOffset}px)` } : undefined}
        data-variant={computedVariant}
        tabIndex={-1}
      >
        {computedVariant === "bottom-sheet" ? (
          <div className="flex justify-center border-b border-transparent pb-2 pt-3">
            <span className="inline-flex h-1.5 w-14 rounded-full bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
          </div>
        ) : null}
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <p className="text-base font-semibold text-slate-900 dark:text-slate-100" id={titleId}>
            {title}
          </p>
          <button
            type="button"
            className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            onClick={onClose}
            aria-label={closeLabel}
          >
            <X {...defaultIconProps} aria-hidden />
          </button>
        </div>
        <div className={contentClasses}>
          {children}
        </div>
      </div>
    </div>,
    modalRoot
  );
}
